import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProviderInvitationsPanel } from '../components/ProviderInvitationsPanel';
import { adminApi } from '../services/api';
import type { AdminLawyer, Company } from '../types';

type SortDirection = 'asc' | 'desc';
type SortField = keyof AdminLawyer | 'companyNumber' | 'companyName' | 'age';

interface ColumnDef {
  field: SortField;
  label: string;
}

const DEFAULT_COLUMN_ORDER: SortField[] = [
  'lawyerNumber', 'verifiedLawyer', 'name', 'organizationNumber',
  'lawyerCompanyName', 'phone', 'vatNumber', 'email',
  'website',
  'address', 'postalCode', 'city', 'createdAt'
];

interface ColumnFilter {
  [key: string]: string;
}

interface AdminCreateLawyerData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  notes?: string;
  companyId: string;
}

export function AdminLawyerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lawyers, setLawyers] = useState<AdminLawyer[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLawyer, setEditingLawyer] = useState<AdminLawyer | null>(null);
  const [selectedLawyerId, setSelectedLawyerId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('lawyerNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter state
  const [filters, setFilters] = useState<ColumnFilter>({});

  // Form state
  const [formData, setFormData] = useState<AdminCreateLawyerData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    notes: '',
    companyId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Column order state
  const [columnOrder, setColumnOrder] = useState<SortField[]>(DEFAULT_COLUMN_ORDER);
  const [draggedColumn, setDraggedColumn] = useState<SortField | null>(null);

  useEffect(() => {
    loadData();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await adminApi.getPreferences();
      if (prefs.adminLawyerListColumns && Array.isArray(prefs.adminLawyerListColumns)) {
        // Validate that all saved columns still exist
        const validColumns = prefs.adminLawyerListColumns.filter(
          (col: string) => DEFAULT_COLUMN_ORDER.includes(col as SortField)
        );
        // Add any new columns that might have been added
        const newColumns = DEFAULT_COLUMN_ORDER.filter(
          col => !validColumns.includes(col)
        );
        setColumnOrder([...validColumns, ...newColumns] as SortField[]);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const saveColumnOrder = useCallback(async (newOrder: SortField[]) => {
    try {
      await adminApi.savePreference('adminLawyerListColumns', newOrder);
    } catch (error) {
      console.error('Failed to save column order:', error);
    }
  }, []);

  const loadData = async () => {
    try {
      const [lawyersData, companiesData] = await Promise.all([
        adminApi.getAllLawyers(),
        adminApi.getCompanyList()
      ]);
      setLawyers(lawyersData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const calculateAgeDays = (dateStr: string): number => {
    if (!dateStr) return 0;
    try {
      const created = new Date(dateStr);
      const today = new Date();
      // Reset time to midnight to only compare dates
      const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const diffTime = todayDate.getTime() - createdDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return 0;
    }
  };

  const getVerificationStatus = (lawyer: AdminLawyer) => {
    if (lawyer.verifiedLawyer === 'Verifierad') {
      return {
        label: t('lawyers.status.verified'),
        className: 'bg-success'
      };
    }
    return {
      label: t('lawyers.status.notVerified'),
      className: 'bg-warning'
    };
  };

  const filteredAndSortedLawyers = useMemo(() => {
    let result = [...lawyers];

    // Apply filters
    Object.entries(filters).forEach(([field, value]) => {
      if (value) {
        result = result.filter((lawyer) => {
          const fieldValue = (lawyer as any)[field];
          // Exact match for verifiedLawyer dropdown filter
          if (field === 'verifiedLawyer') {
            return String(fieldValue) === value;
          }
          return String(fieldValue || '').toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    // Apply sorting
    result.sort((a, b) => {
      // Special handling for age field (calculated from createdAt)
      if (sortField === 'age') {
        const aAge = calculateAgeDays(a.createdAt);
        const bAge = calculateAgeDays(b.createdAt);
        return sortDirection === 'asc' ? aAge - bAge : bAge - aAge;
      }

      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return result;
  }, [lawyers, filters, sortField, sortDirection]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const closeModal = () => {
    setEditingLawyer(null);
    setShowModal(false);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.email?.trim()) {
      errors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.email');
    }
    if (!formData.companyId) {
      errors.companyId = t('validation.required');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    setMessage(null);

    try {
      if (editingLawyer) {
        await adminApi.updateLawyer(editingLawyer.id, formData);
      } else {
        await adminApi.createLawyer(formData);
      }
      await loadData();
      closeModal();
      setMessage({ type: 'success', text: t('lawyers.form.saveSuccess') });
    } catch (error) {
      console.error('Failed to save lawyer:', error);
      setMessage({ type: 'error', text: t('lawyers.form.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRowClick = (lawyerId: string) => {
    setSelectedLawyerId(selectedLawyerId === lawyerId ? null : lawyerId);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <i className="feather-chevrons-up-down ms-1 opacity-25"></i>;
    }
    return sortDirection === 'asc' ? (
      <i className="feather-chevron-up ms-1"></i>
    ) : (
      <i className="feather-chevron-down ms-1"></i>
    );
  };

  const formatDateTime = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  const columnLabels: Record<SortField, string> = {
    companyNumber: t('adminLawyers.columns.companyNumber'),
    companyName: t('adminLawyers.columns.companyName'),
    lawyerNumber: t('lawyers.columns.lawyerNumber'),
    userId: 'User ID',
    name: t('lawyers.columns.name'),
    organizationNumber: t('lawyers.columns.organizationNumber'),
    lawyerCompanyName: t('lawyers.columns.lawyerCompanyName'),
    email: t('lawyers.columns.email'),
    phone: t('lawyers.columns.phone'),
    vatNumber: t('lawyers.columns.vatNumber'),
    website: t('lawyers.columns.website'),
    lawyerPresentation: t('lawyers.columns.lawyerPresentation'),
    verifiedLawyer: t('lawyers.columns.verifiedLawyer'),
    address: t('lawyers.columns.address'),
    postalCode: t('lawyers.columns.postalCode'),
    city: t('lawyers.columns.city'),
    country: t('adminLawyers.colCountry'),
    title: t('adminLawyers.colTitle'),
    experienceYears: t('adminLawyers.colExperienceYears'),
    specialties: t('adminLawyers.colSpecialties'),
    responseTime: t('adminLawyers.colResponseTime'),
    hourlyRate: t('adminLawyers.colHourlyRate'),
    fixedPriceAvailable: t('adminLawyers.colFixedPrice'),
    geographicArea: t('adminLawyers.colGeographicArea'),
    languages: t('adminLawyers.colLanguages'),
    initialMeetingFree: t('adminLawyers.colInitialMeeting'),
    availability: t('adminLawyers.colAvailability'),
    leadTime: t('adminLawyers.colLeadTime'),
    notes: t('lawyers.columns.notes'),
    linkedCustomerIds: t('adminLawyers.colLinkedCustomers'),
    linkedCompanyNumbers: t('adminLawyers.colLinkedCompanies'),
    createdAt: t('adminLawyers.columns.createdAt'),
    age: t('adminLawyers.columns.age'),
    id: 'ID',
    updatedAt: t('adminLawyers.columns.createdAt')
  };

  const columns: ColumnDef[] = columnOrder.map(field => ({
    field,
    label: columnLabels[field] || field
  }));

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, field: SortField) => {
    setDraggedColumn(field);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetField: SortField) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetField) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetField);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    saveColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const exportToExcel = () => {
    // Use columnOrder for headers
    const headers = columnOrder.map(field => columnLabels[field] || field);

    // Helper to get cell value for a field
    const getCellValue = (lawyer: AdminLawyer, field: SortField): string | number => {
      if (field === 'createdAt') return formatDateTime(lawyer.createdAt);
      if (field === 'age') return calculateAgeDays(lawyer.createdAt);
      return (lawyer as any)[field] || '';
    };

    const rows = filteredAndSortedLawyers.map(lawyer =>
      columnOrder.map(field => getCellValue(lawyer, field))
    );

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `admin_lawyers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminLawyers.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminLawyers.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminLawyers.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`} role="alert">
          {message.text}
        </div>
      )}

      <ProviderInvitationsPanel type="lawyer" />

      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2">
            <Link to="/admin/lawyers/create" className="btn btn-primary">
              <i className="feather-plus"></i><span className="d-none d-sm-inline ms-2">{t('lawyers.createLawyer')}</span>
            </Link>
            {selectedLawyerId ? (
              <button className="btn btn-light-brand" onClick={() => navigate(`/admin/lawyers/view/${selectedLawyerId}`)}>
                <i className="feather-eye"></i><span className="d-none d-sm-inline ms-2">{t('lawyers.editLawyer')}</span>
              </button>
            ) : (
              <Button variant="light-brand" disabled>
                <i className="feather-eye"></i><span className="d-none d-sm-inline ms-2">{t('lawyers.editLawyer')}</span>
              </Button>
            )}
            <Button variant="light-brand" onClick={exportToExcel}>
              <i className="feather-download"></i><span className="d-none d-sm-inline ms-2">{t('lawyers.export')}</span>
            </Button>
          </div>
        </div>

        <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.field}
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.field)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, col.field)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleSort(col.field)}
                    style={{
                      cursor: 'grab',
                      whiteSpace: 'nowrap',
                      backgroundColor: draggedColumn === col.field ? '#e3f2fd' : undefined,
                      transition: 'background-color 0.2s'
                    }}
                    title={t('adminLawyers.dragToReorder')}
                  >
                    <i className="feather-move me-1" style={{ opacity: 0.4, fontSize: '12px' }}></i>
                    {col.label}
                    {renderSortIcon(col.field)}
                  </th>
                ))}
              </tr>
              <tr>
                {columns.map((col) => (
                  <th key={`filter-${col.field}`} className="p-1">
                    {col.field === 'verifiedLawyer' ? (
                      <select
                        className="form-select form-select-sm"
                        value={filters[col.field] || ''}
                        onChange={(e) => handleFilterChange(col.field, e.target.value)}
                      >
                        <option value="">{t('lawyers.allStatuses')}</option>
                        <option value="Ej Verifierad">{t('lawyers.status.notVerified')}</option>
                        <option value="Verifierad">{t('lawyers.status.verified')}</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('lawyers.search')}
                        value={filters[col.field] || ''}
                        onChange={(e) => handleFilterChange(col.field, e.target.value)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLawyers.length === 0 ? (
                <tr>
                  <td colSpan={columnOrder.length} className="text-center text-muted py-4">
                    {lawyers.length === 0 ? t('lawyers.noLawyers') : t('projects.noResults')}
                  </td>
                </tr>
              ) : (
                filteredAndSortedLawyers.map((lawyer) => {
                  const verificationStatus = getVerificationStatus(lawyer);
                  return (
                    <tr
                      key={lawyer.id}
                      onClick={() => handleRowClick(lawyer.id)}
                      onDoubleClick={() => navigate(`/admin/lawyers/view/${lawyer.id}`)}
                      className={selectedLawyerId === lawyer.id ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      {columnOrder.map((field) => {
                        if (field === 'createdAt') {
                          return <td key={field}>{formatDateTime(lawyer.createdAt)}</td>;
                        }
                        if (field === 'age') {
                          return <td key={field}>{calculateAgeDays(lawyer.createdAt)}</td>;
                        }
                        if (field === 'verifiedLawyer') {
                          return (
                            <td key={field}>
                              <span className={`badge ${verificationStatus.className}`}>
                                {verificationStatus.label}
                              </span>
                            </td>
                          );
                        }
                        return <td key={field}>{(lawyer as any)[field]}</td>;
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </div>

        <div className="d-md-none">
          <div className="mb-3">
            <div className="row g-2">
              <div className="col-8">
                <input type="text" className="form-control form-control-sm" placeholder={t('lawyers.search')} value={filters['name'] || ''} onChange={(e) => handleFilterChange('name', e.target.value)} />
              </div>
              <div className="col-4">
                <select className="form-select form-select-sm" value={filters['verifiedLawyer'] || ''} onChange={(e) => handleFilterChange('verifiedLawyer', e.target.value)}>
                  <option value="">{t('lawyers.allStatuses')}</option>
                  <option value="Ej Verifierad">{t('lawyers.status.notVerified')}</option>
                  <option value="Verifierad">{t('lawyers.status.verified')}</option>
                </select>
              </div>
            </div>
          </div>
          {filteredAndSortedLawyers.length === 0 ? (
            <p className="text-muted text-center py-3">{lawyers.length === 0 ? t('lawyers.noLawyers') : t('projects.noResults')}</p>
          ) : (
            filteredAndSortedLawyers.map((lawyer) => {
              const verificationStatus = getVerificationStatus(lawyer);
              const isSelected = selectedLawyerId === lawyer.id;
              return (
                <div key={lawyer.id} className={`card mb-2 ${isSelected ? 'border-primary' : ''}`} onClick={() => handleRowClick(lawyer.id)} onDoubleClick={() => navigate(`/admin/lawyers/view/${lawyer.id}`)} style={{ cursor: 'pointer' }}>
                  <div className="card-body py-2 px-3">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold text-truncate">#{lawyer.lawyerNumber} {lawyer.name}</span>
                        <span className={`badge ${verificationStatus.className} flex-shrink-0`}>{verificationStatus.label}</span>
                      </div>
                      {lawyer.lawyerCompanyName && (
                        <div className="text-muted small"><i className="feather-briefcase me-1"></i>{lawyer.lawyerCompanyName}</div>
                      )}
                      {lawyer.email && (
                        <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{lawyer.email}</div>
                      )}
                      {lawyer.phone && (
                        <div className="text-muted small"><i className="feather-phone me-1"></i>{lawyer.phone}</div>
                      )}
                      {(lawyer.address || lawyer.city) && (
                        <div className="text-muted small text-truncate"><i className="feather-map-pin me-1"></i>{[lawyer.address, lawyer.postalCode, lawyer.city].filter(Boolean).join(', ')}</div>
                      )}
                      <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDateTime(lawyer.createdAt)}</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingLawyer ? t('lawyers.form.editTitle') : t('lawyers.form.createTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* Company Selection */}
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">
                        {t('adminLawyers.form.company')} <span className="text-danger">*</span>
                      </label>
                      <select
                        name="companyId"
                        className={`form-select ${formErrors.companyId ? 'is-invalid' : ''}`}
                        value={formData.companyId}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">{t('adminLawyers.form.selectCompany')}</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.companyNumber} - {company.companyName}
                          </option>
                        ))}
                      </select>
                      {formErrors.companyId && (
                        <div className="invalid-feedback">{formErrors.companyId}</div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <FormInput
                        name="name"
                        label={t('lawyers.columns.name')}
                        value={formData.name || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <FormInput
                        name="email"
                        type="email"
                        label={t('lawyers.columns.email')}
                        value={formData.email}
                        onChange={handleFormChange}
                        error={formErrors.email}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-6">
                      <FormInput
                        type="tel"
                        name="phone"
                        label={t('lawyers.columns.phone')}
                        value={formData.phone || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <AddressAutocomplete
                        name="address"
                        onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                        label={t('lawyers.columns.address')}
                        value={formData.address || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-6">
                      <FormInput
                        name="postalCode"
                        label={t('lawyers.columns.postalCode')}
                        value={formData.postalCode || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <FormInput
                        name="city"
                        label={t('lawyers.columns.city')}
                        value={formData.city || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label">{t('lawyers.columns.notes')}</label>
                    <textarea
                      name="notes"
                      className="form-control"
                      rows={3}
                      value={formData.notes || ''}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <Button variant="light-brand" type="button" onClick={closeModal}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" isLoading={isSaving}>
                    <i className="feather-save me-2"></i>
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
